import { FormGroup, FormControl, Validators } from '@angular/forms';
import {
   ViewContainerRef,
   ComponentFactory,
   ComponentFactoryResolver
} from '@angular/core';

// config
import { jsonFormConfig } from '../config/json-form.config';

import { JssNodeAbstract } from './jss-tree-abstract';
import { JssTreeObject } from './jss-tree-object';
import { JssNodeLeaf } from './jss-tree-leaf';
import { JssTreeString } from './jss-tree-string';
import { JssNodeBoolean } from './jss-tree-boolean';
import { JssNodeEnum } from './jss-tree-enum';
import { JssNodeNumber } from './jss-tree-number';
import { LytTreeContainer } from './lyt-tree-container';
import { LytTreeControl } from './lyt-tree-control';
import { JssTreeLayout } from './jss-tree-layout';
import { JssTreeObjectForm } from './jss-tree-object-form';
import { LOCAL_PATH_ROOT_REF_SIGN, LOCAL_PATH_ROOT_OBJECT } from './jss-types';

export class JssEngine {

   // JS object for schema, layout, data
   private _jsonschema: any = null;
   private _jsonlayout: any = null;
   private _jsondata: any = null;

   private _jssTreeRoot: JssTreeObject = new JssTreeObject('root');
   // if no UI-Schema is specified -> just a row layout is taken
   private _lytTreeRoot: LytTreeContainer = new LytTreeContainer('ROW');

   constructor(
      private _localPathRootRefSign: string = LOCAL_PATH_ROOT_REF_SIGN,
      private _localPathRootObjectName: string = LOCAL_PATH_ROOT_OBJECT
   ) { }

   // tree root
   public getTreeRoot(): JssTreeObject { return this._jssTreeRoot; }

   // reset tree

   public getRootLytContainer(): LytTreeContainer {
      if (!this._lytTreeRoot) {
         console.warn('JssEngine: miss layout tree root');
      }

      // if no UI-Scheam is defined -> no children of root
      // -> take roo with 'row' defined
      if (!this._lytTreeRoot.hasChildren()) {
         return this._lytTreeRoot;
      }

      const lytCont = this._lytTreeRoot.getFirstChildContainer();
      if (!lytCont) {
         console.warn('JssEngine: miss layout container');
      }
      return lytCont;
   }

   public dumpJssTree() {
      this._jssTreeRoot.recveDumpJssTree('');
   }

   public dumpLytTree() {
      this._lytTreeRoot.reveDumpLytTree('');
   }

   // search for leafs in tree
   private resolvePathToLeafNode(pathFragments: string[]): JssNodeLeaf {
      // sanity check
      if (pathFragments.length === 0) {
         console.warn('JssEngine: Miss passed path fragments');
         return null;
      }
      // path start with '#' -> take localPathRootNode object
      if (pathFragments[0] === this._localPathRootRefSign) {
         const node = this._jssTreeRoot.reveFindLocalPathRootNode();
         if (node && pathFragments.length > 1) {
            const jssAbst = node.reveFindChildByPath(pathFragments.slice(1));
            if (jssAbst && jssAbst instanceof JssNodeLeaf) {
               return jssAbst as JssNodeLeaf;
            }
         }
      }
      return null;
   }

   public dumpContent() {
      // dump
      console.log('### Dump layoutTree of jssEngine');
      this.dumpLytTree();

      // dump
      console.log('### Dump jssTree of jssEngine');
      this.dumpJssTree();
   }

   public recveGenFormTree() {
      this._jssTreeRoot.recveGenFormTree();
   }

   /**
   * Erzeugt ein JS Object mit dem Inhalt der Form sowie den Hidden-pass-through Daten.
   *
   * @param data: any
   * @returns void
   */
   public recveCreateDataObject(): any {
      // sanity
      if (!this._jssTreeRoot) { return; }

      const node: JssTreeObject = this._jssTreeRoot.reveFindLocalPathRootNode();
      if (!node) {
         console.warn('Error while creating form result object');
         return;
      }

      // create empty data object
      const result: any = {
         // data: {}
      };

      // recursive
      node.recveCreateDataObject(result);

      // data object has 'schema' key, rename property to 'data'
      if (result.schema) {
         Object.defineProperty(result, 'data', Object.getOwnPropertyDescriptor(result, 'schema'));
         delete result.schema;
      }
      return result;
   }

   public parse(
      jsonSchema: any,
      jsonLayout: any,
      jsonData: any
   ): boolean {
      this._jsonschema = jsonSchema;
      let resSchema = false;

      // add form to root
      const treeForm = new JssTreeObjectForm('treeForm');
      this._jssTreeRoot.addChild(treeForm);

      // Schema
      if (jsonFormConfig.debugParseSchema) { console.log('### JssEngine: Start parsing schema'); }
      resSchema = this.parseSchema(
         treeForm,                              // output: tree tructure during parseing
         jsonSchema,                            // input: object json schema
         'schema',                              // nodeName of root
         '');                                   // indent at root level

      // evaluate schema after parsing
      this.evalSchema(true);

      // Layout
      if (jsonLayout) {
         if (jsonFormConfig.debugParseLayout) { console.log('[JssEngine] Start parsing UI-Schema'); }
         this.parseLayout(
            this._jssTreeRoot,                  // output: schema tree
            this._lytTreeRoot,                  // output: layout tree
            jsonLayout,                         // input: object json schema
            '',                                 // indent at root level
            jsonFormConfig.debugParseLayout     // enable/disable debug output
         );
      } else {
         if (jsonFormConfig.debugParseLayout) { console.log('[JssEngine] no layout section found'); }
      }

      // Data
      if (jsonData) {
         if (jsonFormConfig.debugParseData) { console.log('[JssEngine] Start parsing Data'); }
         this.parseData(
            this._jssTreeRoot,                  // output: schema tree
            jsonData                            // input: object json schema
         );
      } else {
         if (jsonFormConfig.debugParseData) { console.log('[JssEngine] no data section found'); }
      }

      return resSchema;
   }

   public evalSchema(
      dump_output: boolean = false
   ) {
      this._jssTreeRoot.recveEvalSchema(dump_output);
   }

   private parseSchema(
      jsonFormParent: JssTreeObject,
      jsonObject: any,
      jsonObjectName: string,
      indent: string = ''     // formatting indent of each level while debugging
   ): boolean {
      const debug = jsonFormConfig.debugParseSchema;

      // debug
      if (debug) {
         console.log(indent + 'Object:"' + jsonObjectName + '"<' + typeof jsonObject + '>');
      }

      // JS object
      if (typeof jsonObject === 'object') {

         // debug
         if (debug) {
            console.log(indent + '{');
         }
         const identInner = indent + '  ';

         // -- create treeNode -------
         // "type": "object"
         // "properties": {}
         // "required": [ <properties> ]
         const jssNode = new JssTreeObject(jsonObjectName);
         jsonFormParent.addChild(jssNode);

         // define if object is root for local path reference
         jssNode.localPathRefRoot =
            (jsonObjectName === this._localPathRootObjectName);


         for (const item in jsonObject) {
            if (jsonObject.hasOwnProperty(item)) {
               const jsonProperty = jsonObject[item];

               switch (item) {
                  case 'type':
                     // type partition -> 'string'
                     if (typeof jsonProperty === 'string') {
                        // debug
                        if (debug) {
                           console.log(identInner + item + '<' + typeof jsonObject[item] + '> = "' + jsonObject[item] + '"');
                        }

                        // store flag in treeNode
                        jssNode.setJssTypeObjectDetected();
                     }
                     break;
                  case '$schema':
                     // $schema partition
                     if (typeof jsonProperty === 'string') {
                        // debug
                        if (debug) {
                           console.log(identInner + item + '<' + typeof jsonObject[item] + '> = "' + jsonObject[item] + '"');
                        }
                     }
                     break;
                  case 'title':
                     // title partition
                     if (typeof jsonProperty === 'string') {
                        // debug
                        if (debug) {
                           console.log(identInner + item + '<' + typeof jsonObject[item] + '> = "' + jsonObject[item] + '"');
                        }

                        // set properties
                        if (jsonObject[item]) {
                           jssNode.title = jsonObject[item];
                        }
                     }
                     break;
                  case 'properties':
                     // properties partition -> object
                     if (typeof jsonProperty === 'object') {
                        // debug
                        if (debug) {
                           console.log(identInner + item + '<' + typeof jsonObject[item] + '> = ');
                        }

                        this.parseSchemaProperties(jsonProperty, jssNode, identInner);
                     }
                     break;
                  default:
                     if (debug) {
                        console.warn(identInner + 'Unknown: ' + item + '<' + typeof jsonObject[item] + '> = ' + jsonObject[item]);
                     }

               }
            }
         }

         if (debug) {
            console.log(indent + '}');
         }
      }

      return true;
   }

   private parseLayout(
      jsonFormParent: JssTreeObject,
      lytTreeParent: LytTreeContainer,
      jsonInput: any,
      indent: string = '',     // formatting indent of each level while debugging
      debug_output: boolean
   ): boolean {


      // -- sanity - JS object --------------------------------------------------------------------
      if (typeof jsonInput !== 'object') {
         console.warn('Miss object of Layout');
         return false;
      }

      // debug
      if (debug_output) {
         console.log(indent + '<' + typeof jsonInput + '>');
         console.log(indent + '{');
      }
      const identInner = indent + '   ';

      // -- title/subtitle of form ----------------------------------------------------------------
      let formTitle = '';
      let formSubTitle = '';
      if (jsonInput.title && typeof jsonInput.title === 'string') {
         formTitle = jsonInput.title;

         // subtitle
         if (jsonInput.subtitle && typeof jsonInput.subtitle === 'string') {
            formSubTitle = jsonInput.subtitle;
         }

         // inject to form
         const refNode: JssNodeAbstract = this._jssTreeRoot.findDirectChildByName('treeForm');
         if (!refNode) {
            console.warn('Can not find Node object "treeForm"');
         } else {
            const refObjectForm: JssTreeObjectForm = refNode as JssTreeObjectForm;
            if (!refObjectForm) {
               console.warn('Can not case to "JssTreeObjectForm"');
            } else {
               refObjectForm.formTitle = formTitle;
               refObjectForm.formSubtitle = formSubTitle;
            }
         }
      }

      // type -> ROW or COLUMN
      if (
         (jsonInput.type.toUpperCase() === 'VERTICALLAYOUT' || jsonInput.type.toUpperCase() === 'HORIZONTALLAYOUT') &&
         typeof jsonInput.type === 'string' &&
         jsonInput.elements &&
         typeof jsonInput.elements === 'object'
      ) {
         // -- create layout node -------
         const lytContainter = new LytTreeContainer(
            (jsonInput.type.toUpperCase() === 'VERTICALLAYOUT') ? 'COLUMN' : 'ROW');
         lytTreeParent.addContainer(lytContainter);

         // debug
         if (debug_output) {
            console.log(identInner + 'type:' + jsonInput.type +
               ';elements<' + typeof jsonInput.elements + '>.length=' +
               jsonInput.elements.length);
         }

         // parse elements of layout
         const items = jsonInput.elements;
         let ctrlOrder = 1;
         let pathFragments: string[] = [];
         let pathRaw = '';

         /* tslint:disable */
         for (const item of items) {

            // Control
            if (
               typeof item === 'object' && item.type && (item.type.toUpperCase() === 'CONTROL'
                  || item.type.toUpperCase() === 'SHOW-ONLY')
            ) {
               // for dump
               let s = identInner + '   ';

               // Scope && $ref
               if (item.scope && item.scope.$ref) {

                  pathRaw = item.scope.$ref;
                  pathFragments = pathRaw.split('/');

                  // for dump
                  s += 'type:' + item.type + ';' + ctrlOrder +
                     ';scope:' + pathFragments;

                  // fetch JssTree
                  const refNodeLeaf: JssNodeLeaf = this.resolvePathToLeafNode(pathFragments);
                  if (!refNodeLeaf) {
                     console.warn('Can not find jssLeafChild with path:' + pathFragments);
                     continue;
                  }
                  refNodeLeaf.enabled = item.type.toUpperCase() != 'SHOW-ONLY';
                  // set order
                  refNodeLeaf.lytOrder = ctrlOrder;

                  // label<boolean> -> default = true
                  if (typeof item.label === 'boolean' && item.label === false) {
                     refNodeLeaf.showTitle = item.label;
                     s += ';label:false';
                  }

                  // label<string>
                  if (typeof item.label === 'string') {
                     refNodeLeaf.title = item.label;
                  }

                  // options.multi
                  if (item.options && item.options.multi === true) {
                     refNodeLeaf.optionMulti = true;
                     s += ';multi:' + item.options.multi;
                  }

                  // create treeControl
                  const lytControl: LytTreeControl = lytContainter.addControl(
                     new LytTreeControl(refNodeLeaf)) as LytTreeControl;

                  // rule
                  if (
                     item.rule &&
                     item.rule.effect &&
                     item.rule.condition &&
                     item.rule.condition.scope.$ref &&
                     item.rule.condition.expectedValue
                  ) {
                     const rule = item.rule;

                     // target
                     let targetPathFragments: string[] = [];
                     let targetPathRaw = '';
                     targetPathRaw = rule.condition.scope.$ref;
                     targetPathFragments = targetPathRaw.split('/');

                     // fetch JssTreeLeaf
                     const targetNode: JssNodeLeaf = this.resolvePathToLeafNode(targetPathFragments);
                     if (!targetNode) {
                        console.warn('Can not find jssLeafChild with path:' + targetPathFragments);
                        continue;   // skip rule
                     }

                     // register rule and subject
                     const effect: string = item.rule.effect.toUpperCase();
                     const data: string = item.rule.condition.expectedValue;
                     refNodeLeaf.eventRegisterRule({
                        effectType: effect,
                        effectCondition: 'EXPECTED_VALUE',
                        emittingNodeName: targetNode.nodeName,
                        data: data
                     });
                     targetNode.registerObserver('VALUE_CHANGED', refNodeLeaf);
                  }

               }

               // dump
               if (debug_output) {
                  console.log(s);
               }

               // hidden-pass-through
            } else if (
               typeof item === 'object' && item.type && item.type.toUpperCase() === 'HIDDEN-PASS-THROUGH'
            ) {
               // for dump
               let s = identInner + '   ';

               // Scope && $ref
               if (item.scope && item.scope.$ref) {

                  pathRaw = item.scope.$ref;
                  pathFragments = pathRaw.split('/');

                  // for dump
                  s += 'type:' + item.type + ';scope:' + pathFragments;

                  // fetch JssTree
                  const refNodeLeaf: JssNodeLeaf = this.resolvePathToLeafNode(pathFragments);
                  if (!refNodeLeaf) {
                     console.warn('Can not find jssLeafChild with path:' + pathFragments);
                  }

                  // set pass-through flag
                  refNodeLeaf.valuePassThrough = true;
               }

               // dump
               if (debug_output) {
                  console.log(s);
               }

               // Layout
            } else if (
               typeof item === 'object' &&
               item.type &&
               (item.type.toUpperCase() === 'VERTICALLAYOUT' || item.type.toUpperCase() === 'HORIZONTALLAYOUT')
            ) {
               // debug
               if (debug_output) {
                  console.log(identInner + '   ' + 'type:' +
                     item.type);
               }

               // recursive call
               this.parseLayout(
                  jsonFormParent,
                  lytContainter,
                  item,
                  identInner + '      ',
                  debug_output
               );

               // Error
            } else {
               console.warn('Unknown object');
            }

            // ctrlIndex
            ctrlOrder += 1;
         }
         /* tslint:enable */

      }

      if (debug_output) {
         console.log(indent + '}');
      }

      return true;
   }

   private parseData(
      jsonFormParent: JssTreeObject,
      jsonObject: any,
   ): boolean {

      // JS object
      if (typeof jsonObject === 'object') {

         // array of objects
         if (
            (jsonObject.type === 'array') &&
            typeof jsonObject.type === 'string' &&
            jsonObject.elements &&
            typeof jsonObject.elements === 'object'
         ) {
            return this.parseDataList(
               this._jssTreeRoot,
               jsonObject,
               ''                                  // formatting indent of each level while debugging
            );

            // data in json format
         } else {
            return this.parseDataJson(
               this._jssTreeRoot,                  // output: schema tree
               jsonObject,                         // input: object json schema
               '#/properties',                     // root
               ''                                  // indent at root level
            );
         }
      }
   }

   private parseDataJson(
      jsonFormParent: JssTreeObject,
      jsonObject: any,
      path,
      indent: string = ''     // formatting indent of each level while debugging
   ): boolean {
      const debug = jsonFormConfig.debugParseData;

      // debug
      if (debug) {
         console.log(indent + '<' + typeof jsonObject + '>');
      }

      // JS object
      if (typeof jsonObject === 'object') {

         // debug
         if (debug) {
            console.log(indent + '{');
         }
         const identInner = indent + '   ';

         let pathFragments: string[] = [];
         let pathRaw = '';

         for (const item in jsonObject) {

            // single value
            if (typeof jsonObject[item] === 'string' || typeof jsonObject[item] === 'boolean') {

               // $ref
               pathRaw = path + '/' + item;
               pathFragments = pathRaw.split('/');

               this.parseNodeLeaf(jsonObject[item], pathFragments);

               // object
            } else if (typeof jsonObject[item] === 'object') {

               // recursive call
               this.parseDataJson(
                  this._jssTreeRoot,
                  jsonObject[item],
                  path + '/' + item,
                  identInner + '   '
               );

               // what is that?
            } else {
               if (debug) {
                  console.warn(identInner + 'Unknown: ' + item + '<' + typeof jsonObject[item] + '> = ' + jsonObject[item]);
               }
            }
         }

         if (debug) {
            console.log(indent + '}');
         }
      }

      return true;
   }

   private parseDataList(
      jsonFormParent: JssTreeObject,
      jsonObject: any,
      indent: string = ''     // formatting indent of each level while debugging
   ): boolean {
      const debug = jsonFormConfig.debugParseData;

      // debug
      if (debug) {
         console.log(indent + '<' + typeof jsonObject + '>');
      }

      // JS object
      if (typeof jsonObject === 'object') {

         // debug
         if (debug) {
            console.log(indent + '{');
         }
         const identInner = indent + '   ';

         // array of objects
         if (
            jsonObject.type === 'array' &&
            jsonObject.elements &&
            typeof jsonObject.elements === 'object'
         ) {
            // parse elements of layout
            const items = jsonObject.elements;
            let pathFragments: string[] = [];
            let pathRaw = '';

            /* tslint:disable */
            for (const item of items) {

               // Value
               if (
                  typeof item === 'object' &&
                  item.value
               ) {
                  // for dump
                  let s = identInner + '   ';

                  // $ref
                  if (item.$ref) {

                     pathRaw = item.$ref;
                     pathFragments = pathRaw.split('/');

                     // for dump
                     s += 'value:' + item.value +
                        ';ref:' + pathFragments;

                     this.parseNodeLeaf(item.value, pathFragments);
                  }

                  // dump
                  if (debug) {
                     console.log(s);
                  }

                  // Error
               } else {
                  console.warn('Unknown object');
               }
            }
            /* tslint:enable */

         }

         if (debug) {
            console.log(indent + '}');
         }
      }

      return true;
   }

   private parseNodeLeaf(leafValue: any, pathFragments: string[]) {
      const debug = jsonFormConfig.debugParseSchema;


      // fetch JssTree
      const refNodeLeaf: JssNodeLeaf = this.resolvePathToLeafNode(pathFragments);
      if (!refNodeLeaf) {
         console.warn('Can not find jssLeafChild with path:' + pathFragments);
         return;
      }

      switch (refNodeLeaf.nodeType) {
         case 'STRING':
            if (typeof leafValue === 'string') {
               (refNodeLeaf as JssTreeString).value = leafValue;
            } else {
               if (debug) { console.warn('Data Type for BOOLEAN not: string'); }
            }
            break;
         case 'BOOLEAN':
            if (typeof leafValue === 'string') {
               if (leafValue === 'true') {
                  (refNodeLeaf as JssNodeBoolean).value = true;
               } else if (leafValue === 'false') {
                  (refNodeLeaf as JssNodeBoolean).value = false;
               } else {
                  if (debug) { console.warn('Data Value for BOOLEAN not: true | false'); }
               }
            } else if (typeof leafValue === 'boolean') {
               (refNodeLeaf as JssNodeBoolean).value = leafValue;
            } else {
               if (debug) { console.warn('Data Type for BOOLEAN not: string | boolean'); }
            }
            break;
         case 'ENUM':
            if (typeof leafValue === 'string') {
               (refNodeLeaf as JssNodeEnum).value = leafValue;
            } else {
               if (debug) { console.warn('Data Type for ENUM not: string'); }
            }
            break;
         case 'INTEGER':
            if (typeof leafValue === 'string') {
               (refNodeLeaf as JssNodeNumber).value = parseInt(leafValue, 10);
            } else if (typeof leafValue === 'number') {
               (refNodeLeaf as JssNodeNumber).value = leafValue;
            } else {
               if (debug) { console.warn('Data Type for INTEGER not: string | number'); }
            }
            break;
         case 'NUMBER':
            if (typeof leafValue === 'string') {
               (refNodeLeaf as JssNodeNumber).value = parseFloat(leafValue);
            } else if (typeof leafValue === 'number') {
               (refNodeLeaf as JssNodeNumber).value = leafValue;
            } else {
               if (debug) { console.warn('Data Type for NUMBER not: string | number'); }
            }
            break;
         default:
            if (debug) {
               console.warn('Unknown Node Type: ' + refNodeLeaf.nodeType);
            }
      }
   }

   private parseSchemaProperties(properties: any, jssNode: JssTreeObject, identInner: string) {
      const debug = jsonFormConfig.debugParseSchema;

      const jssLayout0 = new JssTreeLayout('layoutnode 0');
      jssLayout0.title = 'Dialog mit Layout0';
      jssNode.addChild(jssLayout0);
      const jssNodeToAddChild = jssLayout0;

      for (const req in properties) {
         if (properties.hasOwnProperty(req)) {
            const property = properties[req];

            if (typeof property === 'object') {
               switch (property.type) {

                  case 'string':

                     if (property.enum) {
                        // -- properties.enum (as string) -----------------
                        // create treeControl
                        const treeEnumControl: JssNodeEnum = jssNodeToAddChild.addChild(
                           new JssNodeEnum(req, 'ENUM')) as JssNodeEnum;
                        treeEnumControl.options = property.enum;

                        // set properties
                        if (property.label) {
                           treeEnumControl.title = property.label;
                        }
                        if (property.default) {
                           treeEnumControl.default = property.default;
                        }

                     } else {
                        // -- properties.string ---------------------------
                        // create treeControl
                        const treeStringControl: JssTreeString = jssNodeToAddChild.addChild(
                           new JssTreeString(req, 'STRING')) as JssTreeString;
                        // set properties
                        if (property.format) {
                           treeStringControl.format = property.format;
                        }
                        if (property.minLength) {
                           treeStringControl.minimum = property.minLength;
                           jssNode.addJssRequiredRef(req);
                        }
                        if (property.maxLength) {
                           treeStringControl.maximum = property.maxLength;
                        }
                     }
                     break;

                  case 'integer':
                  case 'number':
                     // -- properties.number ---------------------------
                     // create treeControl
                     const treeNumberControl: JssNodeNumber = jssNodeToAddChild.addChild(
                        new JssNodeNumber(req, 'NUMBER')) as JssNodeNumber;

                     // properties
                     if (property.maximum) {
                        treeNumberControl.maximum = property.maximum;
                     }
                     if (property.minimum) {
                        treeNumberControl.minimum = property.minimum;
                     }
                     if (property.default) {
                        treeNumberControl.default = property.default;
                     }
                     break;

                  case 'boolean':
                     // -- properties.boolean --------------------------
                     // create treeControl
                     const treeBooleanControl: JssNodeBoolean = jssNodeToAddChild.addChild(
                        new JssNodeBoolean(req, 'BOOLEAN')) as JssNodeBoolean;
                     break;

                  case 'array':
                     // -- properties.array ----------------------------
                     if (debug) {
                        console.log(identInner +
                           '  Key:' + req + '<' + typeof property + '>' +
                           ', type=array');
                     }
                     break;

                  case 'object':
                     // -- properties.object ---------------------------
                     // recursive call
                     this.parseSchema(
                        jssNode,
                        property,
                        req,
                        identInner + '    '
                     );
                     break;

                  default:
                     console.warn(identInner + '  Key:' + req + '<' + typeof property + '>=', property);
                     break;
               }
            } else {
               console.warn(identInner + '  Key:' + req + '<' + typeof property + '>=', property);
            }
         }
      }
   }

}



// WEBPACK FOOTER //
// /builds/cp/frap/t3/planer/src/app/core/json-form/model/jss-engine.ts